import { Link } from 'react-router-dom';
import { StarIcon } from 'lucide-react';
import { REVIEWS_CLAUSE } from '../../data/legal';
import { founder, salon } from '../../data/content';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { shopLabels } from '../../data/shopLabels';
import type { Product, Review } from '../../core/types/catalog';
import type { Creator } from '../../core/types/site';
type Props = {
  product: Product;
  reviews: Review[];
  /** Een vaste cliënt die dit product als haar favoriet heeft (Instellingen › Cadeaus & codes). Geen review: zonder sterren. */
  client?: Pick<Creator, 'name' | 'quote'>;
};

/** Ervaringen: het gemiddelde links (met eventueel de favoriet van een vaste cliënt), geschreven reviews rechts. */
export function ProductReviews({
  product,
  reviews,
  client
}: Props) {
  return <section id="reviews" className="scroll-mt-28 border-t border-line bg-sand py-16 lg:py-20" aria-labelledby="reviews-title" data-id="element-8126" data-mp="129ahh5od24hv">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8127" data-mp="16abcydbvbcrd">
        <div className="lg:col-span-4" data-id="element-8128" data-mp="1b8wecg2uw2he">
          <h2 id="reviews-title" className={product.reviews > 0 ? 'sr-only' : 'type-sub text-ink'} data-id="element-8129" data-mp="1b7v3ablubfzo">
            Ervaringen
          </h2>
          {product.reviews > 0 && <>
              <p className="type-figure text-ink" aria-hidden="true" data-id="element-8130" data-mp="127exgvhmjwrn">
                {rating(product.rating)}
              </p>
              <p className="mt-2 text-[15px] text-muted" data-id="element-8131" data-mp="ni90le69eimx">
                <span className="sr-only" data-id="element-8132" data-mp="sak6cxnokyne">{rating(product.rating)} van 5, </span>
                gemiddeld uit {plural(product.reviews, 'beoordeling', 'beoordelingen')}
              </p>
            </>}
          {client && <figure className="mt-8 border-t border-ink/15 pt-5" data-id="element-8133" data-mp="ikqqvlkf3zdv">
              <figcaption className="text-[14px] text-ink" data-id="element-8134" data-mp="4lr30g4qboir">Favoriet van {client.name}, vaste cliënt</figcaption>
              <blockquote className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-8135" data-mp="9ihkn7w6ms6a">“{client.quote}”</blockquote>
            </figure>}
        </div>
        <ul className="grid gap-8 sm:grid-cols-2 lg:col-span-8" data-id="element-8136" data-mp="26ii50ashza9p">
          {reviews.map(r => <li key={`${r.name}-${r.body.slice(0, 24)}`} className="border-t border-ink/15 pt-5" data-id="element-8137" data-mp="22geqfdaj7pk7">
              {/* Altijd vijf sterren, de lege gedempt: zo lees je "4 van 5" in één oogopslag. */}
              <p className="flex gap-0.5" data-id="element-8138" data-mp="2u7kjxavqyp0i">
                <span className="sr-only" data-id="element-8139" data-mp="2u6ja19qgw8kd">{r.rating} van 5</span>
                {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 fill-current ${i < r.rating ? 'text-clay' : 'text-ink/15'}`} strokeWidth={0} aria-hidden="true" data-id="element-8140" data-mp="2q44mwdfrmmna" data-mpi={i} />)}
              </p>
              <blockquote className="mt-3 type-name text-ink" data-id="element-8141" data-mp="2g7t73dgyveo3">“{r.body}”</blockquote>
              <p className="mt-3 text-[14px] text-muted" data-id="element-8142" data-mp="19tk74jpgaw8b">
                {r.name} · {r.context}
                {r.verified && ' · geverifieerd'}
              </p>
            </li>)}
          {reviews.length === 0 && <li className="text-[15px] leading-relaxed text-muted sm:col-span-2" data-id="element-8143" data-mp="un4pgkvvqq9o">
              Nog geen geschreven review voor dit product. Gebruik je het al?{' '}
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-8144" data-mp="14lkiwuidqbmc">
                Laat {founder.firstName} weten wat je ervan vindt
                <span className="sr-only" data-id="element-8145" data-mp="1dens2qlzs2b6">{shopLabels.viaWhatsApp}</span>
              </a>
              .
            </li>}
        </ul>
        {/* Omnibus: zeggen hoe reviews gecontroleerd worden, met de clausule erbij. */}
        {reviews.length > 0 && <p className="text-[13px] leading-relaxed text-muted lg:col-span-8 lg:col-start-5" data-id="element-8146" data-mp="1sd9dkhiobc6g">
            Alleen wie het product kocht of de behandeling had, kan een review plaatsen. {founder.firstName} plaatst positieve én kritische reviews, ongewijzigd.{' '}
            <Link to={REVIEWS_CLAUSE} className="text-ink underline underline-offset-4" data-id="element-8147" data-mp="3u674g7sw3uor">
              Zo controleer ik reviews
            </Link>
          </p>}
      </div>
    </section>;
}